import React, { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layouts';
import { adminApi } from '../../api/adminApi';
import Avatar from '../../components/Avatar';
import Modal from '../../components/Modal';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Select,
  TabButton,
  Tabs,
  Textarea,
  Toast,
  cn,
} from '../../components/ui';

const DOC_LABELS = { id: 'Government ID', insurance: 'Public Liability Insurance' };
const DOC_TYPES = ['id', 'insurance'];
const STATES = ['QLD', 'NSW', 'VIC', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const STATUS_META = {
  pending: { label: 'Pending', variant: 'warning', border: 'border-amber-200' },
  approved: { label: 'Approved', variant: 'success', border: 'border-emerald-200' },
  rejected: { label: 'Rejected', variant: 'danger', border: 'border-red-200' },
  not_submitted: { label: 'Not submitted', variant: 'neutral', border: 'border-slate-200' },
};

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const AdminVerifications = () => {
  const [providers, setProviders] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [actionBusy, setActionBusy] = useState(null);
  const [rejectingType, setRejectingType] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '', suburb: '', state: 'QLD', postcode: '', status: 'active' });
  const [editSaving, setEditSaving] = useState(false);
  const [reqAddOpen, setReqAddOpen] = useState(false);
  const [reqAddMessage, setReqAddMessage] = useState('');
  const [reqAddSaving, setReqAddSaving] = useState(false);
  const [avatarPreviewOpen, setAvatarPreviewOpen] = useState(false);

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (search.trim()) params.search = search.trim();
      const res = await adminApi.listVerifications(params);
      setProviders(res.data || []);
      setCounts(res.counts || { pending: 0, approved: 0, rejected: 0, total: 0 });
    } catch (err) {
      setListError(err.message || 'Failed to load verifications.');
    } finally {
      setListLoading(false);
    }
  }, [filterStatus, search]);

  useEffect(() => { loadList(); }, [loadList]);

  const loadDetail = useCallback(async (userId) => {
    if (!userId) { setDetail(null); return; }
    setDetailLoading(true);
    setDetailError('');
    try {
      const res = await adminApi.getVerificationDetail(userId);
      setDetail(res.data);
    } catch (err) {
      setDetailError(err.message || 'Failed to load provider details.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const flashMsg = (msg) => { setActionMsg(msg); setTimeout(() => setActionMsg(''), 2500); };
  const refreshAll = async (userId) => Promise.all([loadList(), loadDetail(userId)]);

  const approveDoc = async (type) => {
    if (!detail?._id) return;
    setActionBusy(`doc:${type}`);
    try {
      await adminApi.reviewDocument(detail._id, type, { status: 'approved' });
      await refreshAll(detail._id);
      flashMsg(`${DOC_LABELS[type]} approved.`);
    } catch (err) { flashMsg(err.message || 'Failed to approve.'); }
    finally { setActionBusy(null); }
  };

  const submitReject = async () => {
    if (!detail?._id || !rejectingType || !rejectionReason.trim()) return;
    setActionBusy(`doc:${rejectingType}`);
    try {
      await adminApi.reviewDocument(detail._id, rejectingType, { status: 'rejected', rejectionReason: rejectionReason.trim() });
      await refreshAll(detail._id);
      flashMsg(`${DOC_LABELS[rejectingType]} rejected.`);
      setRejectingType(null);
      setRejectionReason('');
    } catch (err) { flashMsg(err.message || 'Failed to reject.'); }
    finally { setActionBusy(null); }
  };

  const setOverall = async (status) => {
    if (!detail?._id) return;
    setActionBusy(`overall:${status}`);
    try {
      await adminApi.setVerificationStatus(detail._id, status);
      await refreshAll(detail._id);
      flashMsg(`Verification set to ${status}.`);
    } catch (err) { flashMsg(err.message || 'Failed to set status.'); }
    finally { setActionBusy(null); }
  };

  const openEdit = () => {
    if (!detail) return;
    setEditForm({
      name: detail.name || '', phone: detail.phone || '', suburb: detail.location?.suburb || '',
      state: detail.location?.state || 'QLD', postcode: detail.location?.postcode || '', status: detail.status || 'active',
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!detail?._id) return;
    setEditSaving(true);
    try {
      await adminApi.updateUser(detail._id, {
        name: editForm.name,
        phone: editForm.phone,
        location: { suburb: editForm.suburb, state: editForm.state, postcode: editForm.postcode },
        status: editForm.status,
      });
      await refreshAll(detail._id);
      setEditOpen(false);
      flashMsg('Account info updated.');
    } catch (err) { flashMsg(err.message || 'Failed to update account.'); }
    finally { setEditSaving(false); }
  };

  const openRequestAdditional = () => {
    setReqAddMessage(detail?.additionalVerificationRequest?.message || '');
    setReqAddOpen(true);
  };

  const submitRequestAdditional = async () => {
    if (!detail?._id || !reqAddMessage.trim()) return;
    setReqAddSaving(true);
    try {
      await adminApi.requestAdditional(detail._id, reqAddMessage.trim());
      await refreshAll(detail._id);
      setReqAddOpen(false);
      flashMsg('Additional verification request sent.');
    } catch (err) { flashMsg(err.message || 'Failed to send request.'); }
    finally { setReqAddSaving(false); }
  };

  const clearRequestAdditional = async () => {
    if (!detail?._id) return;
    setActionBusy('clearReq');
    try {
      await adminApi.clearAdditionalRequest(detail._id);
      await refreshAll(detail._id);
      flashMsg('Additional verification request cleared.');
    } catch (err) { flashMsg(err.message || 'Failed to clear request.'); }
    finally { setActionBusy(null); }
  };

  const overallMeta = STATUS_META[detail?.verificationStatus] || STATUS_META.pending;
  const activeAddReq = detail?.additionalVerificationRequest?.active;

  return (
    <AdminLayout title="Verification Management">
      {actionMsg && <Toast>{actionMsg}</Toast>}
      <div className="grid min-h-[620px] gap-4 lg:grid-cols-[310px_minmax(0,1fr)]">
        <Card className="overflow-hidden shadow-none">
          <div className="border-b border-slate-100 p-4">
            <h2 className="m-0 font-nunito text-base font-extrabold text-giggs-ink">Providers</h2>
            <Input className="mt-3" placeholder="Search name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className="mt-3 overflow-x-auto pb-1">
              <Tabs className="min-w-max">
                {[
                  ['pending', `Pending (${counts.pending})`],
                  ['approved', `Approved (${counts.approved})`],
                  ['rejected', `Rejected (${counts.rejected})`],
                  ['all', `All (${counts.total})`],
                ].map(([key, label]) => <TabButton key={key} active={filterStatus === key} onClick={() => setFilterStatus(key)}>{label}</TabButton>)}
              </Tabs>
            </div>
          </div>
          <div className="max-h-[620px] overflow-y-auto">
            {listLoading ? <EmptyState title="Loading…" /> : listError ? <Alert variant="danger" className="m-3">{listError}</Alert> : providers.length === 0 ? <EmptyState title="No providers match this filter." /> : providers.map((provider) => {
              const meta = STATUS_META[provider.verificationStatus] || STATUS_META.pending;
              return (
                <button
                  type="button"
                  key={provider._id}
                  onClick={() => setSelectedId(provider._id)}
                  className={cn('flex w-full items-center gap-3 border-0 border-b border-slate-100 px-4 py-3 text-left transition last:border-b-0 hover:bg-slate-50', selectedId === provider._id && 'bg-giggs-green/[0.055]')}
                >
                  <Avatar user={provider} size={36} gradient="linear-gradient(135deg, #C7E8D4, #AAF0C4)" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-dm text-[13px] font-semibold text-giggs-ink">{provider.name}{provider.additionalVerificationRequest?.active && <span className="ml-1 text-amber-600">•</span>}</span>
                    <span className="block truncate font-dm text-[11px] text-giggs-muted">{provider.email}</span>
                  </span>
                  <Badge variant={meta.variant}>{meta.label}</Badge>
                </button>
              );
            })}
          </div>
        </Card>

        <div className="min-w-0">
          {!selectedId ? (
            <Card><EmptyState title="Select a provider" description="Choose a provider from the list to review their verification." /></Card>
          ) : detailLoading ? (
            <Card><EmptyState title="Loading…" /></Card>
          ) : detailError ? (
            <Alert variant="danger">{detailError}</Alert>
          ) : detail ? (
            <div className="flex flex-col gap-4">
              <Card className="overflow-hidden">
                <CardBody className="!p-0">
                  <div className="grid min-w-0 gap-4 p-5 sm:p-6 md:grid-cols-[auto_minmax(0,1fr)] xl:grid-cols-[auto_minmax(0,1fr)_170px] xl:items-start">
                    <div className="shrink-0"><Avatar user={detail} size={64} gradient="linear-gradient(135deg, #C7E8D4, #AAF0C4)" fontSize={26} /></div>
                    <div className="min-w-0 self-center">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <h2 className="m-0 min-w-0 break-words font-nunito text-xl font-extrabold leading-tight text-giggs-ink">{detail.name}</h2>
                        <Badge variant={overallMeta.variant}>{overallMeta.label}</Badge>
                      </div>
                      <div className="mt-2 grid min-w-0 gap-1 text-xs leading-5 text-giggs-muted">
                        <p className="m-0 min-w-0 break-words">{detail.email} · {detail.phone || 'No phone'}</p>
                        <p className="m-0 min-w-0 break-words">{detail.location?.suburb || '—'}{detail.location?.state ? `, ${detail.location.state}` : ''}{detail.location?.postcode ? ` ${detail.location.postcode}` : ''} · Joined {fmtDate(detail.createdAt)}</p>
                      </div>
                    </div>
                    <div className="grid w-full gap-2 md:col-span-2 md:grid-cols-2 xl:col-span-1 xl:grid-cols-1">
                      <Button size="sm" className="w-full" onClick={openEdit}>Edit Account</Button>
                      <Button size="sm" className="w-full" variant="outline" onClick={openRequestAdditional}>Request More Info</Button>
                    </div>
                  </div>

                  {activeAddReq && (
                    <div className="mx-5 mb-5 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:mx-6 sm:mb-6 sm:flex-row sm:items-start">
                      <div className="min-w-0 flex-1">
                        <div className="font-nunito text-sm font-extrabold text-amber-800">Additional verification requested</div>
                        <p className="mb-0 mt-1 whitespace-pre-wrap break-words text-[13px] leading-5 text-amber-900">{detail.additionalVerificationRequest.message}</p>
                        <p className="mb-0 mt-1 text-[11px] text-amber-700">Requested {fmtDate(detail.additionalVerificationRequest.requestedAt)}</p>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2"><Button size="sm" variant="outline" onClick={openRequestAdditional}>Edit</Button><Button size="sm" onClick={clearRequestAdditional} disabled={actionBusy === 'clearReq'}>{actionBusy === 'clearReq' ? 'Clearing…' : 'Mark resolved'}</Button></div>
                    </div>
                  )}

                  <div className="border-t border-slate-100 bg-slate-50/70 px-5 py-4 sm:px-6">
                    <div className="mb-2 text-xs font-semibold text-giggs-muted">Override overall status</div>
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      {['pending', 'approved', 'rejected'].map((status) => {
                        const current = detail.verificationStatus === status;
                        return <Button key={status} size="sm" variant={current ? 'secondary' : status === 'approved' ? 'primary' : status === 'rejected' ? 'danger' : 'outline'} onClick={() => !current && setOverall(status)} disabled={current || actionBusy === `overall:${status}`}>{actionBusy === `overall:${status}` ? 'Setting…' : current ? `Currently ${status}` : `Set ${status}`}</Button>;
                      })}
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardHeader title="Identity & Documents" />
                <CardBody className="flex flex-col gap-3">
                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div><h3 className="m-0 font-nunito text-sm font-extrabold text-giggs-ink">Profile Picture</h3><p className="mb-0 mt-1 text-xs leading-5 text-giggs-muted">Visual reference. Compare against the Government ID below to verify identity match.</p></div>
                      <Badge variant={detail.avatar ? 'success' : 'neutral'}>{detail.avatar ? 'Set' : 'Not set'}</Badge>
                    </div>
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                      <button type="button" className="shrink-0 rounded-full border-0 bg-transparent p-0" onClick={() => detail.avatar && setAvatarPreviewOpen(true)} disabled={!detail.avatar}><Avatar user={detail} size={96} gradient="linear-gradient(135deg, #C7E8D4, #AAF0C4)" fontSize={36} /></button>
                      <div>{detail.avatar ? <><div className="text-[13px] font-semibold text-giggs-ink">Profile photo on file</div><div className="mt-0.5 text-[11px] text-giggs-muted">Last updated {fmtDate(detail.avatarChangedAt)}</div><a href={detail.avatar} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex text-xs font-semibold text-giggs-green underline">Open full size</a></> : <div className="text-[13px] text-giggs-muted">Provider has not uploaded a profile picture yet.</div>}</div>
                    </div>
                  </div>

                  {DOC_TYPES.map((type) => {
                    const doc = detail.verificationDocuments?.[type] || {};
                    const status = doc.status || 'not_submitted';
                    const meta = STATUS_META[status] || STATUS_META.not_submitted;
                    const busy = actionBusy === `doc:${type}`;
                    return (
                      <div key={type} className={cn('rounded-xl border p-4', meta.border)}>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0"><h3 className="m-0 font-nunito text-sm font-extrabold text-giggs-ink">{DOC_LABELS[type]}</h3><p className="mb-0 mt-1 break-words text-xs leading-5 text-giggs-muted">{doc.url ? `${doc.originalName || 'Document'}${doc.submittedAt ? ` · Submitted ${fmtDate(doc.submittedAt)}` : ''}${doc.reviewedAt ? ` · Reviewed ${fmtDate(doc.reviewedAt)}` : ''}` : 'Provider has not uploaded this document yet.'}</p></div>
                          <Badge variant={meta.variant}>{meta.label}</Badge>
                        </div>
                        {status === 'rejected' && doc.rejectionReason && <Alert variant="danger" className="mt-3" title="Rejection reason:">{doc.rejectionReason}</Alert>}
                        {doc.url ? (
                          <div className="mt-4">
                            <div className="flex flex-col gap-3 rounded-xl bg-slate-50 p-3 sm:flex-row sm:items-center">
                              <div className="min-w-0 flex-1"><span className="block truncate text-[13px] font-semibold text-giggs-ink">{doc.originalName || 'Document'}</span><span className="block text-[11px] text-giggs-muted">{(doc.mimeType || '').toUpperCase().replace('APPLICATION/', '').replace('IMAGE/', '') || 'FILE'}</span></div>
                              <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-giggs-green underline">View / Download</a>
                            </div>
                            <div className="mt-3 flex gap-2"><Button size="sm" onClick={() => approveDoc(type)} disabled={busy || status === 'approved'}>{busy ? '…' : status === 'approved' ? 'Approved' : 'Approve'}</Button><Button size="sm" variant="danger" onClick={() => { setRejectingType(type); setRejectionReason(''); }} disabled={busy}>{status === 'rejected' ? 'Update rejection' : 'Reject'}</Button></div>
                          </div>
                        ) : <div className="mt-3 text-xs text-giggs-muted">No file uploaded yet.</div>}
                      </div>
                    );
                  })}
                </CardBody>
              </Card>
            </div>
          ) : null}
        </div>
      </div>

      {rejectingType && (
        <Modal title={`Reject ${DOC_LABELS[rejectingType]}`} size="sm" onClose={() => !actionBusy && setRejectingType(null)}>
          <div className="flex flex-col gap-4 p-5 sm:p-6">
            <Field label="Reason (visible to provider)" hint={`${rejectionReason.length}/500`}><Textarea rows={4} placeholder="e.g. Photo is too blurry. Please re-upload a clearer copy." value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} maxLength={500} autoFocus /></Field>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setRejectingType(null)} disabled={actionBusy}>Cancel</Button><Button variant="danger" onClick={submitReject} disabled={Boolean(actionBusy) || !rejectionReason.trim()}>{actionBusy ? 'Rejecting…' : 'Reject Document'}</Button></div>
          </div>
        </Modal>
      )}

      {reqAddOpen && (
        <Modal title={activeAddReq ? 'Update Request' : 'Request Additional Verification'} size="sm" onClose={() => !reqAddSaving && setReqAddOpen(false)}>
          <div className="flex flex-col gap-4 p-5 sm:p-6">
            <Field label="Message to provider" hint={`${reqAddMessage.length}/1000`}><Textarea rows={5} placeholder="Explain exactly what additional verification is needed." value={reqAddMessage} onChange={(e) => setReqAddMessage(e.target.value)} maxLength={1000} autoFocus /></Field>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setReqAddOpen(false)} disabled={reqAddSaving}>Cancel</Button><Button onClick={submitRequestAdditional} disabled={reqAddSaving || !reqAddMessage.trim()}>{reqAddSaving ? 'Sending…' : activeAddReq ? 'Update Request' : 'Send Request'}</Button></div>
          </div>
        </Modal>
      )}

      {editOpen && (
        <Modal title="Edit Account" onClose={() => !editSaving && setEditOpen(false)}>
          <form onSubmit={saveEdit} className="flex flex-col gap-4 p-5 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2"><Field label="Full Name"><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required /></Field><Field label="Phone"><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></Field></div>
            <div className="grid gap-4 sm:grid-cols-3"><Field label="Suburb"><Input value={editForm.suburb} onChange={(e) => setEditForm({ ...editForm, suburb: e.target.value })} /></Field><Field label="State"><Select value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}>{STATES.map((state) => <option key={state}>{state}</option>)}</Select></Field><Field label="Postcode"><Input maxLength="4" value={editForm.postcode} onChange={(e) => setEditForm({ ...editForm, postcode: e.target.value })} /></Field></div>
            <Field label="Account Status"><Select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}><option value="active">Active</option><option value="suspended">Suspended</option></Select></Field>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setEditOpen(false)} disabled={editSaving}>Cancel</Button><Button type="submit" disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</Button></div>
          </form>
        </Modal>
      )}

      {avatarPreviewOpen && detail?.avatar && (
        <Modal size="lg" onClose={() => setAvatarPreviewOpen(false)}>
          <div className="flex flex-col items-center gap-4 p-4"><img src={detail.avatar} alt="Profile" className="max-h-[72vh] max-w-full rounded-lg object-contain" /><Button onClick={() => setAvatarPreviewOpen(false)}>Close</Button></div>
        </Modal>
      )}
    </AdminLayout>
  );
};

export default AdminVerifications;
